import React from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {modalShow} from '@core/actions/ModalActions';
import PersonalInfo from './fields/PersonalInfo';
import Tooltip from './fields/Tooltip';
import {formatPrice} from '../helpers/PriceHelper';
import AnimatedNumber from '../../common/AnimatedNumber';

function Section({icon, title, sub, extraClass = '', innerRef, children}) {
    return (
        <div ref={innerRef} className={`resume-form__section ${extraClass}`}>
            <div className="resume-form__section-header">
                <div className="resume-form__section-icon">{icon}</div>
                <div className="resume-form__section-head-text">
                    <div className="resume-form__section-title">{title}</div>
                    {sub && <div className="resume-form__section-sub">{sub}</div>}
                </div>
            </div>
            <div className="resume-form__section-body">
                {children}
            </div>
        </div>
    );
}

export default function OrderInfoPage({
                                          options,
                                          selectedFeatures = [],
                                          totalPrice,
                                          authErrors,
                                          isNewCustomerRef,
                                          onClearErrors,
                                          showResetPassword = false,
                                          resetPasswordEmail = '',
                                          onForgotPassword,
                                          onResetPasswordBack,
                                          fileUploadRef,
                                          resetPasswordFormRef,
                                          formRootRef,
                                          setStep,
                                          onOpenFileUpload,
                                      }) {
    const dispatch = useDispatch();
    const {authorized} = useSelector(state => state.user);
    const files = useSelector(state => state.file.data) || [];
    const isGuest = !authorized;

    // Red border when a feature requiring files is selected but no files uploaded yet
    const filesRequired = selectedFeatures.some(key => options.features?.[key]?.fileRequired);
    const hasFileError = filesRequired && files.length === 0;

    const goBack = () => {
        if (showResetPassword) {
            onResetPasswordBack && onResetPasswordBack();
            return;
        }

        setStep(0);
        formRootRef?.current?.scrollIntoView({behavior: 'smooth'});
    };

    return <div className="resume-form__step-second">
        <div className="resume-form__page-header">
            <div className="resume-form__step-badge">
                <span className="resume-form__step-badge-dot"/>
                Step 2 of 2 — Order Information
            </div>
            <h1 className="resume-form__title">Almost there</h1>
            <p className="resume-form__page-subtitle">
                Share a few details so our team can get started on your order
            </p>
        </div>

        <Section
            icon={<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                       strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="17 8 12 3 7 8"/>
                <line x1="12" y1="3" x2="12" y2="15"/>
            </svg>}
            title="Additional Information"
            sub="Upload files that help us learn more about your background (optional)"
        >
            <div
                ref={fileUploadRef}
                className={`resume-form__additional ${hasFileError ? 'resume-form__additional--error' : ''}`}
            >
                <div className="resume-form__additional-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                         strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                        <polyline points="17 8 12 3 7 8"/>
                        <line x1="12" y1="3" x2="12" y2="15"/>
                    </svg>
                </div>
                <div className="resume-form__additional-content">
                    <div className="resume-form__additional-title">
                        Upload materials
                        <Tooltip
                            text="Upload here your old resume or list of your advantages — everything that can help us to learn more about your professional background."/>
                    </div>
                    <div className="resume-form__additional-subtitle">
                        {files.length > 0
                            ? `${files.length} file${files.length !== 1 ? 's' : ''} uploaded`
                            : 'Add your old resume, references, or any supporting documents'}
                    </div>
                </div>
                <button
                    className="resume-form__additional-btn"
                    type="button"
                    onClick={onOpenFileUpload || (() => dispatch(modalShow('fileUpload')))}
                >
                    {files.length > 0 ? 'Add More' : 'Upload Files'}
                </button>
            </div>
        </Section>

        <Section
            icon={<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                       strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                <circle cx="12" cy="7" r="4"/>
            </svg>}
            title="Personal Information"
            sub={isGuest ? 'Sign in or continue as a new customer' : 'This info will be used for your order'}
        >
            <PersonalInfo
                countries={options.countries}
                errors={authErrors}
                isGuest={isGuest}
                isNewCustomerRef={isNewCustomerRef}
                onClearErrors={onClearErrors}
                onForgotPassword={onForgotPassword}
                showResetPassword={showResetPassword}
                resetPasswordEmail={resetPasswordEmail}
                onResetPasswordBack={onResetPasswordBack}
            />
        </Section>

        {!showResetPassword && (
            <div className="resume-form__total-price">
                <div className="resume-form__price text-center">
                    <strong className="label-price">Total</strong>
                    <span className={`amount-price ${totalPrice <= 0 ? 'empty' : ''}`}>
                        <AnimatedNumber value={totalPrice} format={formatPrice}/>
                    </span>
                </div>
                <button className="button-proceed" type="submit">Place An Order</button>
            </div>
        )}
        <div className="resume-form__button-back">
            <button type="button" className="resume-form__back-btn" onClick={goBack}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
                     strokeLinecap="round" strokeLinejoin="round">
                    <line x1="19" y1="12" x2="5" y2="12"/>
                    <polyline points="12 19 5 12 12 5"/>
                </svg>
                Back to Order Details
            </button>
        </div>
    </div>;
}
